import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { baseUrl, getToken } from 'Api';

const PENDING_KEY = 'pendingMembership';

// Single source of truth for membership state — always resolved from the
// backend (never localStorage or a post-payment redirect). Re-fetch after any
// purchase or cancel via the returned refetch().
export default function useMembership() {
  const navigate = useNavigate();
  const [state, setState] = useState({
    loading: true, isMember: false, status: 'none', membership: null, error: '',
  });
  const activeRef = useRef(true);

  // silent = true refreshes in the background (no spinner) so an open member
  // view stays mounted while it reconciles after a cancel/resume.
  const fetchMembership = useCallback(async (silent = false) => {
    setState((s) => ({ ...s, loading: silent ? s.loading : true, error: '' }));
    try {
      const { data: body } = await axios.get(`${baseUrl}/v4/membership/my-membership`, {
        params: { token: getToken() },
      });
      if (!activeRef.current) return;
      const d = body?.data || {};
      setState({
        loading: false,
        isMember: !!d.is_member,
        status: d.status || d.membership?.status || 'none',
        membership: d.membership || null,
        error: '',
      });
      // Once the backend has spoken, drop any stashed checkout plan.
      try { localStorage.removeItem(PENDING_KEY); } catch (e) { /* ignore */ }
    } catch (e) {
      if (!activeRef.current) return;
      if (e?.response?.status === 401) { navigate('/newSign-in'); return; }
      setState({
        loading: false,
        isMember: false,
        status: 'none',
        membership: null,
        error: e?.response?.data?.message || 'Could not load your membership.',
      });
    }
  }, [navigate]);

  useEffect(() => {
    activeRef.current = true;
    fetchMembership();
    return () => { activeRef.current = false; };
  }, [fetchMembership]);

  return { ...state, refetch: fetchMembership };
}
